/**
 * Nova64 Namespace Builder
 *
 * Routes flat API functions into grouped sub-namespaces under `nova64.*`.
 * Flat globals remain available with one-time deprecation warnings.
 */

// ── Namespace mapping: function name → sub-namespace ─────────────────────────
// Each key is a sub-namespace name, each value is the list of function/property
// names from the flat API that belong in that group.

export const NAMESPACE_MAP = {
  draw: [
    // api.js (2D primitives)
    'cls',
    'cls3D',
    'pset',
    'line',
    'rect',
    'rectfill',
    'drawRect',
    'circle',
    'print',
    'packRGBA64',
    'rgba8',
    'setCamera',
    'screenWidth',
    'screenHeight',
    // api-2d.js (shapes, text, HUD)
    'colorLerp',
    'colorMix',
    'hexColor',
    'hslColor',
    'n64Palette',
    'drawGradient',
    'drawRadialGradient',
    'drawSkyGradient',
    'drawFlash',
    'drawRoundedRect',
    'poly',
    'drawTriangle',
    'drawDiamond',
    'drawStarburst',
    'drawWave',
    'drawSpiral',
    'drawCheckerboard',
    'measureText',
    'drawTextBox',
    'textBox',
    'printCentered',
    'printRight',
    'drawGlowText',
    'drawGlowTextCentered',
    'drawPulsingText',
    'drawScanlines',
    'drawNoise',
    'drawProgressBar',
    'drawHealthBar',
    'drawPixelBorder',
    'drawPanel',
    'drawCrosshair',
    'createMinimap',
    'drawMinimap',
    'drawFloatingTexts',
    'drawFloatingTexts3D',
    'scrollingText',
    // api-blend.js
    'BM',
    'setBlendMode',
    'resetBlendMode',
    'withBlend',
    'withAlpha',
  ],

  sprite: [
    'spr',
    'sprRect',
    'mapDraw',
    'loadSprites',
    'loadMap',
    'loadAtlas',
    'sprByName',
    'getAtlasFrame',
    'getSpriteSheetImage',
    'applySpriteSheetDataURL',
  ],

  scene: [
    // 3D primitives & transforms
    'engine',
    'getBackendCapabilities',
    'createCube',
    'createSphere',
    'createCylinder',
    'createPlane',
    'createAdvancedCube',
    'createAdvancedSphere',
    'createTorus',
    'createCone',
    'createCapsule',
    'destroyMesh',
    'removeMesh',
    'loadModel',
    'loadVoxModel',
    'playAnimation',
    'updateAnimations',
    'loadTexture',
    'setPosition',
    'setRotation',
    'setScale',
    'getPosition',
    'getRotation',
    'rotateMesh',
    'moveMesh',
    'setFlatShading',
    'setMeshEmissive',
    'setMeshColor',
    'setMeshVisible',
    'setMeshOpacity',
    'setCastShadow',
    'setReceiveShadow',
    'clearScene',
    // Instancing & LOD
    'createInstancedMesh',
    'setInstanceTransform',
    'setInstanceColor',
    'finalizeInstances',
    'removeInstancedMesh',
    'createLODMesh',
    'setLODPosition',
    'removeLODMesh',
    'updateLODs',
    // PBR
    'loadNormalMap',
    'setNormalMap',
    'setPBRMaps',
    'setPBRProperties',
    // Raycasting & stats
    'raycastFromCamera',
    'get3DStats',
    'setupScene',
    'setClearColor',
    'getScene',
    'getRenderer',
    'getMesh',
  ],

  camera: [
    // 3D camera
    'setCameraPosition',
    'setCameraTarget',
    'setCameraLookAt',
    'setCameraFOV',
    'getCamera',
    // 2D camera
    'createCamera2D',
    'beginCamera2D',
    'endCamera2D',
    'cam2DApply',
    'cam2DReset',
    'cam2DFollow',
    'cam2DShake',
    'updateCamera2D',
    'cam2DWorldToScreen',
    'cam2DScreenToWorld',
    'cam2DGetBounds',
  ],

  light: [
    // Lighting
    'setLightDirection',
    'setLightColor',
    'setAmbientLight',
    'setDirectionalLight',
    'createPointLight',
    'setPointLightPosition',
    'setPointLightColor',
    'setLightVisible',
    'removeLight',
    // Fog
    'setFog',
    'clearFog',
    // Skybox
    'createSpaceSkybox',
    'createGradientSkybox',
    'createSolidSkybox',
    'createImageSkybox',
    'animateSkybox',
    'setSkyboxSpeed',
    'enableSkyboxAutoAnimate',
    'disableSkyboxAutoAnimate',
    'clearSkybox',
  ],

  fx: [
    // Post-processing (api-effects.js)
    'enableBloom',
    'disableBloom',
    'setBloomStrength',
    'setBloomRadius',
    'setBloomThreshold',
    'enableFXAA',
    'disableFXAA',
    'enableChromaticAberration',
    'disableChromaticAberration',
    'enableVignette',
    'disableVignette',
    'enableGlitch',
    'disableGlitch',
    'setGlitchIntensity',
    'glitchBurst',
    // Babylon-specific bonus (graceful no-op on Three.js)
    'enableGlow',
    'disableGlow',
    'enableRetroEffects',
    'disableRetroEffects',
    'isEffectsEnabled',
    'setEffectsBypass',
    'isEffectsBypassed',
    'renderEffects',
    // Pixelation/dithering (api-3d.js)
    'enablePixelation',
    'enableDithering',
    // Presets
    'enableN64Mode',
    'enablePSXMode',
    'enableLowPolyMode',
    'disablePresetMode',
    // Filters (api-filters.js)
    'F',
    'CM',
    'withFilter',
    'withColorMatrix',
    'applyColorMatrix',
    // 2D particles
    'createEmitter2D',
    'burstEmitter2D',
    'setEmitter2DActive',
    'updateEmitter2D',
    'drawEmitter2D',
    'getParticleCount',
    'clearEmitter2D',
    // 3D particles
    'createParticleSystem',
    'setParticleEmitter',
    'emitParticle',
    'burstParticles',
    'updateParticles',
    'removeParticleSystem',
    'getParticleStats',
  ],

  shader: [
    // Custom shaders (api-effects.js)
    'createShaderMaterial',
    'updateShaderUniform',
    // TSL (api-3d.js)
    'createTSLMaterial',
    'createTSLShaderMaterial',
    '_updateTSLMaterials',
    'createLavaMaterial',
    'createVortexMaterial',
    'createPlasmaMaterial',
    'createWaterMaterial',
    'createHologramMaterial',
    'createShockwaveMaterial',
    'tslFn',
    'tslUniform',
    'tslFloat',
    'tslInt',
    'tslVec2',
    'tslVec3',
    'tslVec4',
    'tslColor',
    'tslSin',
    'tslCos',
    'tslMix',
    'tslStep',
    'tslSmoothstep',
    'tslClamp',
    'tslFract',
    'tslFloor',
    'tslAbs',
    'tslPow',
    'tslHash',
    'tslUv',
    'tslTime',
    'tslPositionLocal',
    'tslPositionWorld',
    'tslNormalLocal',
    'tslNormalWorld',
    'tslLoop',
  ],

  input: [
    'btn',
    'btnp',
    'key',
    'keyp',
    'isKeyDown',
    'isKeyPressed',
    'mouseX',
    'mouseY',
    'mouseDown',
    'mousePressed',
    'gamepadAxis',
    'gamepadConnected',
    'leftStickX',
    'leftStickY',
    'rightStickX',
    'rightStickY',
    // MediaPipe body tracking
    'initHandTracking',
    'getHandLandmarks',
    'getHandGesture',
    'initFaceTracking',
    'getFaceLandmarks',
    'getFaceBlendShapes',
    'initPoseTracking',
    'getPoseLandmarks',
    'startCamera',
    'stopCamera',
    'getCameraTexture',
    'showCameraBackground',
    'hideCameraBackground',
    'stopTracking',
  ],

  audio: ['sfx', 'setVolume'],

  physics: [
    'createBody',
    'destroyBody',
    'stepPhysics',
    'setGravity',
    'setTileSize',
    'setTileSolidFn',
    'setCollisionMap',
    // Collision helpers
    'aabb',
    'circleCollision',
    'raycastTilemap',
  ],

  voxel: [
    'BLOCK_TYPES',
    'updateVoxelWorld',
    'forceLoadVoxelChunks',
    'getVoxelBlock',
    'setVoxelBlock',
    'raycastVoxelBlock',
    'checkVoxelCollision',
    'checkVoxelFluid',
    'moveVoxelEntity',
    'placeVoxelTree',
    'resetVoxelWorld',
    'configureVoxelWorld',
    'getVoxelConfig',
    'getVoxelHighestBlock',
    'getVoxelBiome',
    'getVoxelLightLevel',
    'setVoxelDayTime',
    'getVoxelNoaPrototypeStatus',
    'probeVoxelNoaPrototype',
    'saveVoxelWorld',
    'loadVoxelWorld',
    'listVoxelWorlds',
    'deleteVoxelWorld',
    'registerVoxelBlock',
    'getVoxelBlockShape',
    'getVoxelBlockBoundingBox',
    'isVoxelBlockFullCube',
    'VOXEL_SHAPE_BBOXES',
    'enableVoxelTextures',
    'loadVoxelTextureAtlas',
    'spawnVoxelEntity',
    'removeVoxelEntity',
    'getVoxelEntity',
    'damageVoxelEntity',
    'healVoxelEntity',
    'updateVoxelEntities',
    'getVoxelEntitiesInRadius',
    'getVoxelEntitiesByType',
    'getVoxelEntityCount',
    'cleanupVoxelEntities',
    'setVoxelEntityComponent',
    'getVoxelEntityComponent',
    'hasVoxelEntityComponent',
    'removeVoxelEntityComponent',
    'queryVoxelEntities',
    'createVoxelEntityArchetype',
    'spawnVoxelEntityFromArchetype',
    'findVoxelPath',
    'exportVoxelRegion',
    'importVoxelRegion',
    'exportVoxelWorldJSON',
    'importVoxelWorldJSON',
    'simplexNoise2D',
    'simplexNoise3D',
    'setVoxelFluidSource',
    'removeVoxelFluidSource',
    'getVoxelFluidLevel',
    'importVoxModel',
  ],

  ui: [
    // ui.js
    'setFont',
    'getFont',
    'setTextAlign',
    'setTextBaseline',
    'drawText',
    'drawTextShadow',
    'drawTextOutline',
    'createPanel',
    'drawAllPanels',
    'removePanel',
    'clearPanels',
    'createButton',
    'updateButton',
    'drawButton',
    'updateAllButtons',
    'drawAllButtons',
    'removeButton',
    'clearButtons',
    'uiProgressBar',
    'drawGradientRect',
    'centerX',
    'centerY',
    'grid',
    'setMousePosition',
    'setMouseButton',
    'getMousePosition',
    'isMouseDown',
    'isMousePressed',
    'uiColors',
    'uiFonts',
    // screens.js
    'ScreenManager',
    'Screen',
    'screens',
    'addScreen',
    'switchToScreen',
    'switchScreen',
    'transitionTo',
    'onTransitionEnd',
    'isTransitioning',
    'getCurrentScreen',
    'startScreens',
    // canvas-ui.js
    'parseCanvasUI',
    'updateCanvasUI',
    'renderCanvasUI',
    'destroyCanvasUI',
    // stage.js
    'createContainer',
    'createSpriteNode',
    'createGraphicsNode',
    'createTextNode',
    'addChild',
    'removeChild',
    'removeAllChildren',
    'setChildIndex',
    'drawStage',
    'hitTest',
    // movie-clip.js
    'createMovieClip',
    'playClip',
    'pauseClip',
    'stopClip',
    'gotoAndPlay',
    'gotoAndStop',
    'updateClips',
    'drawClip',
    // editor
    'openSpriteEditor',
    'closeSpriteEditor',
    // textinput
    'startTextInput',
    'stopTextInput',
    'getTextInput',
  ],

  tween: [
    'Ease',
    'createTween',
    'killTween',
    'killTweensOf',
    'killAllTweens',
    'updateTweens',
    'getTweenCount',
    // hype.js
    'hypeRegister',
    'hypeUnregister',
    'hypeUpdate',
    'hypeReset',
    'createOscillator',
    'createTimeTrigger',
    'createRandomTrigger',
    'createProximityTrigger',
    'createHSwarm',
    'createColorPool',
    'createHPool',
    'createGridLayout',
    'createCircleLayout',
    'createSphereLayout',
    'createPathLayout',
  ],

  data: [
    // storage.js
    'saveData',
    'loadData',
    'deleteData',
    'saveJSON',
    'loadJSON',
    'remove',
    // store.js
    'createGameStore',
    'novaStore',
    // manifest / env / i18n / data / assets
    'getMeta',
    'loadEnv',
    'setLevel',
    'getEnv',
    'getLevel',
    'getLevels',
    'getCheats',
    'setCheat',
    't',
    'setLocale',
    'getLocale',
    'getAvailableLocales',
    'addStrings',
    'getEnemy',
    'getNPC',
    'getBoss',
    'getEnemies',
    'getEnemiesByTier',
    'getNPCs',
    'getBosses',
    'getItem',
    'getItems',
    'getItemsByType',
    'getItemsByRarity',
    'getUIConfig',
    'getGameplay',
    'preloadAssets',
    'getAsset',
    'getAssetStatus',
    // nft-seed.js
    'createSeedFromHash',
    'createSeedRNG',
    'seedToTraits',
    'exportSeedMetadata',
    'setSeed',
    'getSeed',
    'getSeedRNG',
    // wad.js
    'WADLoader',
    'WADTextureManager',
    'convertWADMap',
    'setWallUVs',
    'THING_MONSTERS',
    'THING_ITEMS',
    'THING_SPRITE_PREFIX',
  ],

  util: [
    // api-2d.js math helpers
    'lerp',
    'clamp',
    'randRange',
    'randInt',
    'dist',
    'dist3d',
    'remap',
    'pulse',
    'deg2rad',
    'rad2deg',
    // api-generative.js
    'TWO_PI',
    'HALF_PI',
    'QUARTER_PI',
    'noise',
    'noiseSeed',
    'noiseDetail',
    'noiseMap',
    'ellipse',
    'ellipsefill',
    'arc',
    'bezier',
    'quadCurve',
    'pushMatrix',
    'popMatrix',
    'translate',
    'rotate',
    'scale2d',
    'resetMatrix',
    'colorMode',
    'color',
    'hsb',
    'lerpColor',
    'ease',
    'smoothstep',
    'flowField',
    'frameCount',
    // api-gameutils.js
    'createShake',
    'triggerShake',
    'updateShake',
    'getShakeOffset',
    'createCooldown',
    'useCooldown',
    'cooldownReady',
    'cooldownProgress',
    'updateCooldown',
    'createCooldownSet',
    'updateCooldowns',
    'createHitState',
    'triggerHit',
    'isInvulnerable',
    'isVisible',
    'isFlashing',
    'updateHitState',
    'createSpawner',
    'updateSpawner',
    'triggerWave',
    'getSpawnerWave',
    'createPool',
    'createFloatingTextSystem',
    'createStateMachine',
    'createTimer',
  ],

  xr: [
    'enableVR',
    'enableAR',
    'enableCardboardVR',
    'disableXR',
    'isXRActive',
    'isXRSupported',
    'getXRSession',
    'getXRMode',
    'getXRControllers',
    'getXRHands',
    'setXRReferenceSpace',
    'setCameraRigPosition',
  ],
};

/**
 * Build the namespaced `nova64` object from a flat API and the mapping config.
 * Functions that exist in the flat API but not in the mapping are collected
 * into `nova64._unmapped` for debugging.
 *
 * @param {Object} flatApi - The flat nova64api object with all functions
 * @param {Object} mapping - NAMESPACE_MAP
 * @returns {Object} The nova64 namespace object
 */
export function buildNamespace(flatApi, mapping) {
  const ns = {};
  const mapped = new Set();

  for (const [group, keys] of Object.entries(mapping)) {
    const sub = {};
    for (const k of keys) {
      if (k in flatApi) {
        const desc = Object.getOwnPropertyDescriptor(flatApi, k);
        if (desc && (desc.get || desc.set)) {
          Object.defineProperty(sub, k, desc);
        } else {
          sub[k] = flatApi[k];
        }
        mapped.add(k);
      }
    }
    ns[group] = sub;
  }

  // Collect unmapped keys for debugging
  const unmapped = {};
  for (const k of Object.keys(flatApi)) {
    if (!mapped.has(k)) {
      unmapped[k] = flatApi[k];
    }
  }
  if (Object.keys(unmapped).length > 0) {
    ns._unmapped = unmapped;
  }

  return ns;
}
